import { useState, useEffect, useId } from 'react'
import { Button, Modal, Select } from '@/components/ui'
import { SectionLoader } from '@/components/common/SectionLoader'
import type { SelectOption } from '@/components/ui'
import type { UserStatus } from '@/types'
import { useUser, useChangeUserStatus } from '../hooks/useUsers'

type MutableUserStatus = Exclude<UserStatus, 'exited'>

const STATUS_OPTIONS: SelectOption[] = [
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'suspended', label: 'Suspended' },
]

export interface ChangeUserStatusModalProps {
  userId: string | null
  onClose: () => void
  onSuccess: () => void
}

export function ChangeUserStatusModal({
  userId,
  onClose,
  onSuccess,
}: ChangeUserStatusModalProps): React.ReactElement {
  const formId = useId()
  const { data: user, isLoading: userLoading } = useUser(userId)
  const changeStatus = useChangeUserStatus()
  const [selectedStatus, setSelectedStatus] = useState<string>(() => user?.status ?? 'active')

  useEffect(() => {
    if (!user?.status) return
    setSelectedStatus(user.status === 'exited' ? 'inactive' : user.status)
  }, [user?.status])

  const open = !!userId

  const handleSubmit = (e: React.FormEvent): void => {
    e.preventDefault()
    if (!userId || !selectedStatus) return
    changeStatus.mutate(
      { id: userId, payload: { status: selectedStatus as MutableUserStatus } },
      { onSuccess: () => onSuccess() }
    )
  }

  const showFooter = Boolean(user && !userLoading)

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Change user status"
      className="max-w-sm"
      footer={
        showFooter ? (
          <>
            <Button type="button" variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button form={formId} type="submit" loading={changeStatus.isPending}>
              {changeStatus.isPending ? 'Saving…' : 'Save'}
            </Button>
          </>
        ) : null
      }
    >
      {!userId ? null : userLoading ? (
        <SectionLoader variant="detail" rows={2} label="Loading user" />
      ) : user ? (
        <form id={formId} onSubmit={handleSubmit} className="space-y-5">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Update status for <span className="font-medium text-gray-900 dark:text-gray-100">{user.firstName} {user.lastName}</span>.
          </p>
          <Select
            label="Status"
            options={STATUS_OPTIONS}
            value={selectedStatus}
            onChange={setSelectedStatus}
          />
        </form>
      ) : null}
    </Modal>
  )
}

ChangeUserStatusModal.displayName = 'ChangeUserStatusModal'
